import { useEffect, useState } from "react";
import type { FramerateName, TimecodeSpec } from "@infoflyt/shared/timecode";
import { formatTimecode, timecodeAt } from "@infoflyt/shared/timecode";

/**
 * Tidskoden, og BARE den, tikker.
 *
 * Laa foer i App, og da gjengav hele treet seg ti ganger i sekundet — begge
 * sidene, alle kortene. Under en blagest er det ekstra arbeid paa hovedtraaden
 * hvert hundrede millisekund, og det traff paa et tilfeldig punkt i
 * bevegelsen. Her er det ett `<span>` som gjengis.
 *
 * ANKERET ER PRODUKSJONENS, ikke et froe.
 *
 * Her sto «ankeret er froeet til nettflata faar synk»: et fast tidspunkt i
 * oktober, med tidskoden paa null. Toppstripa tikket derfor ekte, men viste
 * en helt annen tidskode enn den som ble lagret i loggen — og enda en enn
 * appen. Naa leses produksjonens jam. Rettet 25.10.2026.
 *
 * Staar det ingen jam for dagen, staar det streker. Et tall uten anker er
 * verre enn ingenting: det ser like ekte ut som det riktige.
 *
 * ========  OG ROEDT NAAR JAMMEN ER FOR GAMMEL  ========
 *
 * Brukeren 01.11.2026: «Hvis tidskoden ikke er jammet av en tekniker de
 * siste 24 timene, saa skal tidskoden vaere roed.» Et doegn er en
 * opptaksdag: boksene er slaatt av og paa siden i gaar, og avviket er
 * ukjent. Tallet tikker videre — det er fortsatt det beste vi har — men
 * det sier at det ikke er avtalt med noen i dag.
 *
 * `anker.wallUtcMs` er jammens EGEN tid her: `jamFor` henter jam-raden,
 * ikke en adopsjon av den. Appen maa baere tidspunktet gjennom
 * adopsjonen i stedet — se StoredAnchor.jammetUtcMs.
 */
const GAMMEL_ETTER_MS = 24 * 3_600_000;
export function Tidskode({
  anker, framerate, dropFrame, spec,
}: {
  readonly anker: {
    readonly tcFrames: number;
    readonly monotonicMs: number;
    readonly bootId: string;
    readonly wallUtcMs: number;
  } | null;
  readonly framerate: FramerateName;
  readonly dropFrame: boolean;
  readonly spec: TimecodeSpec;
}): JSX.Element {
  const [naa, setNaa] = useState(() => Date.now());

  // Ti ganger i sekundet, som i appen: tidskoden skal leses, ikke flimre.
  useEffect(() => {
    const id = window.setInterval(() => setNaa(Date.now()), 100);
    return () => window.clearInterval(id);
  }, []);

  if (anker === null) {
    return <span className="tidskode mono">--:--:--:--</span>;
  }

  /*
   * Veggklokka er bindeleddet.
   *
   * Ankerets monotone telling hoerer til enheten som satte det, og denne
   * maskinen har ikke den. `timecodeAt` ser at boot-id-en er en annen og
   * regner forloept tid fra veggklokka — samme regning som en loggpost
   * herfra faar.
   */
  const tc = timecodeAt(
    { ...anker, framerate, dropFrame, source: "manual" },
    { monotonicMs: 0, bootId: "nett", wallUtcMs: naa },
    spec,
  );

  const gammel = naa - anker.wallUtcMs >= GAMMEL_ETTER_MS;

  return (
    <span className={`tidskode mono${gammel ? " tidskode--gammel" : ""}`}>
      {formatTimecode(tc.frames, spec)}
    </span>
  );
}
